// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, Tooltip } from "@mui/material";
import { useStoreState } from "easy-peasy";
import FullPageLoader from "components/Loader/FullPage";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CustomModal from "components/CustomModal";
import { get, post } from "requests";
import { Add, Autorenew, Expand, Fullscreen } from "@mui/icons-material";

interface MoneyThumbTabProps {
  dealApplicationId: number;
  isReadOnly?: boolean;
  permittedTabs?: any;
}

type MoneyThumbStatus = "loading" | "processing" | "success" | "error" | "not_found";

export default function MoneyThumbTab({ dealApplicationId, isReadOnly }: MoneyThumbTabProps) {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<MoneyThumbStatus>("loading");
  const [message, setMessage] = useState<string>("");
  const [retryCount, setRetryCount] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRunningMoneyThumb, setIsRunningMoneyThumb] = useState(false);

  const mediaAttachments = useStoreState((state: any) => state.fileUploads.mediaAttachments);
  const [files, setFiles] = useState<any[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [showMoneyThumbModal, setShowMoneyThumbModal] = useState(false);

  useEffect(() => {
    if (Array.isArray(mediaAttachments) && mediaAttachments.length > 0) {
      const statementsFile = mediaAttachments.filter(
        (file: any) => file.category === "Bank Statements"
      );
      setFiles(statementsFile);
    } else {
      setFiles([]);
    }
  }, [mediaAttachments]);

  useEffect(() => {
    fetchEmbedUrl();
  }, [dealApplicationId]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;

    if (status === "processing") {
      intervalId = setInterval(() => {
        fetchEmbedUrl();
        setRetryCount((prev) => prev + 1);
      }, 8000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [status]);

  const fetchEmbedUrl = async () => {
    try {
      setStatus("loading");

      const response = await get(`moneythumb/getEmbedURL/${dealApplicationId}`);

      const apiStatus = response?.status;
      const apiMessage = response?.message;
      const apiData = response?.data;

      if (apiStatus === "success") {
        setEmbedUrl(apiData?.url || null);
        setStatus("success");
        return;
      }

      if (apiStatus === "processing") {
        setStatus("processing");
        setMessage("MoneyThumb is processing the statements. Please wait…");
        return;
      }

      if (apiStatus === "not_found") {
        setStatus("not_found");
        setMessage("MoneyThumb analysis has not been run yet for this application.");
        return;
      }

      setStatus("error");
      setMessage(apiMessage || "Unable to load MoneyThumb viewer.");
    } catch (err) {
      setStatus("error");
      setMessage("Network error while loading embed URL.");
    }
  };

  const runMoneyThumb = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setMessage("Please select at least one statement file before running MoneyThumb.");
      return;
    }

    try {
      setIsRunningMoneyThumb(true);
      setStatus("loading");
      setMessage("Uploading statements and starting MoneyThumb analysis…");

      const response = await post(
        "moneythumb/uploadApplicationStatements",
        {
          appId: dealApplicationId,
          mediaAttachmentIds: selectedFiles,
        },
        { timeout: 0 }
      );

      const isAsync = response?.async === true || response?.async_accepted === true;

      if (isAsync) {
        setStatus("processing");
        setMessage("MoneyThumb is processing your statements. This may take a few moments.");
      } else {
        setStatus("success");
        setMessage("MoneyThumb successfully processed your statements!");
      }

      fetchEmbedUrl();
    } catch (error) {
      console.error("MoneyThumb error:", error);
      setStatus("error");
      setMessage("Unable to start MoneyThumb analysis. Please try again.");
    } finally {
      setIsRunningMoneyThumb(false);
      setShowMoneyThumbModal(false);
      setSelectedFiles([]);
    }
  };

  const handleFileSelect = (event: any, newValue: any) => {
    setSelectedFiles(newValue.map((option: any) => option.value));
  };

  if (status === "loading") {
    return <FullPageLoader />;
  }

  if (status === "processing") {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        p={3}
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <MDBox position="relative" mb={3}>
          <MDBox
            sx={{
              width: 80,
              height: 80,
              border: "4px solid",
              borderColor: "grey.300",
              borderTopColor: "primary.main",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        </MDBox>

        <MDTypography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
          Processing MoneyThumb Data
        </MDTypography>

        <MDTypography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 400, textAlign: "center", mb: 3 }}
        >
          MoneyThumb is analyzing your bank statements. This usually takes a few moments.
        </MDTypography>

        <MDButton
          variant="contained"
          color="primary"
          onClick={fetchEmbedUrl}
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: 2,
            fontWeight: "bold",
          }}
        >
          Check Again
        </MDButton>
      </MDBox>
    );
  }

  if (status === "not_found") {
    return (
      <>
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="flex-end"
          minHeight="120px"
          p={3}
        >
          <MDButton
            variant="contained"
            color="primary"
            onClick={() => {
              setSelectedFiles([]);
              setShowMoneyThumbModal(true);
            }}
            disabled={isRunningMoneyThumb}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 1,
              textTransform: "none",
              fontWeight: "bold",
              boxShadow: 2,
              "&:hover": {
                boxShadow: 3,
              },
            }}
            startIcon={
              isRunningMoneyThumb ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="none"
                  />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="currentColor"
                  />
                  <path
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="none"
                  />
                </svg>
              )
            }
          >
            {isRunningMoneyThumb ? "Starting..." : "Run MoneyThumb"}
          </MDButton>
        </MDBox>

        <CustomModal
          open={showMoneyThumbModal}
          onClose={() => {
            setSelectedFiles([]);
            setShowMoneyThumbModal(false);
          }}
          title="Upload Bank Statements (MoneyThumb)"
          cancelText="Cancel"
          submitText="Submit"
          onCancel={() => {
            setSelectedFiles([]);
            setShowMoneyThumbModal(false);
          }}
          onSubmit={runMoneyThumb}
          maxWidth="sm"
          fullWidth
        >
          <MDBox display="flex" flexDirection="column" width="100%">
            <Autocomplete
              multiple
              id="moneythumb-file-select"
              options={files.map((file) => ({
                value: file.id,
                label: file.name,
              }))}
              getOptionLabel={(option) => option.label}
              value={files
                .filter((file) => selectedFiles.includes(file.id))
                .map((file) => ({
                  value: file.id,
                  label: file.name,
                }))}
              onChange={handleFileSelect}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField {...params} label="Select Bank Statement Files" />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              sx={{ mt: 2, mb: 2 }}
            />
          </MDBox>
        </CustomModal>
      </>
    );
  }

  if (status === "error") {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="300px"
        p={4}
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "error.light",
        }}
      >
        <MDBox sx={{ color: "error.main", mb: 2 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
        </MDBox>

        <MDTypography variant="h5" color="error" gutterBottom sx={{ fontWeight: "bold" }}>
          Error Loading MoneyThumb
        </MDTypography>

        <MDTypography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
          {message}
        </MDTypography>

        <MDButton variant="contained" color="primary" onClick={fetchEmbedUrl}>
          Retry
        </MDButton>
      </MDBox>
    );
  }

  if (status === "success" && embedUrl) {
    return (
      <MDBox
        sx={{
          position: isExpanded ? "fixed" : "relative",
          inset: isExpanded ? 0 : "auto",
          zIndex: isExpanded ? 2000 : "auto",
          backgroundColor: isExpanded ? "background.default" : "transparent",
          p: isExpanded ? 0 : 2,
        }}
      >
        {!isExpanded && (
          <MDBox
            sx={{
              position: "absolute",
              top: 42,
              right: 32,
              zIndex: 2001,
              display: "flex",
              flexDirection: "row",
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <Tooltip title="Add Statements" placement="bottom" arrow>
              {isRunningMoneyThumb ? (
                <Autorenew
                  onClick={() => setShowMoneyThumbModal(true)}
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    fontSize: "30px !important",
                    animation: "spin 1.5s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              ) : (
                <Add
                  onClick={() => setShowMoneyThumbModal(true)}
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    fontSize: "32px !important",
                    "& svg": {
                      strokeWidth: 3,
                      stroke: "currentColor",
                      filter: "drop-shadow(0px 1px 1px rgba(0,0,0,0.1))",
                    },
                    "&:hover": {
                      color: "primary.dark",
                      "& svg": {
                        strokeWidth: 5,
                      },
                    },
                  }}
                />
               
              )}
            </Tooltip>

            <Tooltip title="Expand to Full Screen" placement="bottom" arrow>
              <Fullscreen
                onClick={() => setIsExpanded(true)}
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  fontSize: "32px !important",
                  padding: 0.5,
                  borderRadius: "8px",
                  "&:hover": {
                    color: "primary.dark",
                    backgroundColor: "rgba(47, 146, 159, 0.08)",
                  },
                }}
              />
            </Tooltip>
          </MDBox>
        )}

        {isExpanded && (
          <Tooltip title="Exit Full Screen" placement="left" arrow>
            <MDButton
              variant="contained"
              color="error"
              onClick={() => setIsExpanded(false)}
              sx={{
                position: "fixed",
                top: 20,
                right: 20,
                zIndex: 2001,
                minWidth: "auto",
                width: 40,
                height: 40,
                borderRadius: "50%",
                p: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: 4,
                "&:hover": {
                  boxShadow: 6,
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 9l6 6m0-6l-6 6M4 8V4m0 0h4M4 4l6 6m10-6h-4m4 0v4m0-4l-6 6M4 16v4m0 0h4M4 20l6-6m10 6h-4m4 0v-4m0 4l-6-6"
                  stroke="white"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </MDButton>
          </Tooltip>
        )}

        <MDBox
          sx={{
            width: "100%",
            height: isExpanded ? "100vh" : "calc(100vh - 260px)",
            borderRadius: isExpanded ? 0 : "12px",
            overflow: "hidden",
            border: isExpanded ? "none" : "1px solid rgba(0,0,0,0.08)",
            boxShadow: isExpanded ? "none" : "0 2px 12px rgba(0,0,0,0.04)",
            position: "relative",
            transition: "all 0.3s ease",
            mt: isExpanded ? 0 : 2,
          }}
        >
          <iframe
            src={embedUrl}
            title="MoneyThumb Viewer"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
        </MDBox>

        <CustomModal
          open={showMoneyThumbModal}
          onClose={() => {
            setSelectedFiles([]);
            setShowMoneyThumbModal(false);
          }}
          title="Upload Bank Statements (MoneyThumb)"
          cancelText="Cancel"
          submitText="Submit"
          onCancel={() => {
            setSelectedFiles([]);
            setShowMoneyThumbModal(false);
          }}
          onSubmit={runMoneyThumb}
          maxWidth="sm"
          fullWidth
        >
          <MDBox display="flex" flexDirection="column" width="100%">
            <Autocomplete
              multiple
              id="moneythumb-file-select"
              options={files.map((file) => ({
                value: file.id,
                label: file.name,
              }))}
              getOptionLabel={(option) => option.label}
              value={files
                .filter((file) => selectedFiles.includes(file.id))
                .map((file) => ({
                  value: file.id,
                  label: file.name,
                }))}
              onChange={handleFileSelect}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField {...params} label="Select Bank Statement Files" />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              sx={{ mt: 2, mb: 2 }}
            />
          </MDBox>
        </CustomModal>
      </MDBox>
    );
  }

  return null;
}
